<template>
  <div
    :class="classes(n())"
    role="listbox"
    :aria-label="`Column ${columnIndex}`"
    :aria-activedescendant="activeDescendantId"
  >
    <div
      ref="scrollRef"
      :class="n('scroll')"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <div
        :class="n('options')"
        :style="wrapperStyle"
      >
        <div
          v-for="(option, index) in options"
          :id="`picker-col-${columnIndex}-opt-${index}`"
          :key="getOptionValue(option)"
          :class="classes(
            n('option'),
            {
              [n('option--selected')]: index === currentIndex,
              [n('option--disabled')]: isOptionDisabled(option),
            },
            option.className
          )"
          role="option"
          :aria-selected="index === currentIndex ? 'true' : 'false'"
          :aria-disabled="isOptionDisabled(option) ? 'true' : undefined"
          @click="handleClickOption(option, index)"
        >
          <slot
            name="option"
            :option="option"
            :index="index"
            :column-index="columnIndex"
          >
            <span :class="n('option-text')">{{ getOptionText(option) }}</span>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
  defineComponent,
} from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../../utils/components';
import { useColumnTouch } from './use-column-touch';
import type { PickerOption } from '../types';

/**
 * BEM 命名辅助
 * PickerColumn 在 DOM 中归属于 picker 的 BEM 体系，
 * 因此使用 pickerNs 生成类名（picker__column / picker__column__xxx / picker__column--xxx）
 */
const pickerNs = createNamespace('picker');
const { name } = createNamespace('picker-column');

const n = (suffix?: string): string => {
  if (!suffix) return pickerNs.n('column');
  if (suffix.startsWith('--')) return `${pickerNs.n('column')}${suffix}`;
  return `${pickerNs.n('column')}__${suffix}`;
};
const { classes } = pickerNs;

export default defineComponent({
  name,
  props,
  emits,
  setup(props, { emit }) {
    /** 滚动容器 DOM 引用，用于绑定 touch 事件和测量选项高度 */
    const scrollRef = ref<HTMLElement | null>(null);

    /** 当前选中项的索引 */
    const currentIndex = ref(props.defaultIndex);

    /** 从 DOM 实际测量到的单个选项高度（px），用于精确的滚动计算 */
    const measuredItemHeight = ref(0);

    // ==================== 字段名映射 ====================

    /** 解析后的自定义字段名（text / value），支持 columnsFieldNames 配置 */
    const fieldNames = computed(() => ({
      text: props.columnsFieldNames?.text || 'text',
      value: props.columnsFieldNames?.value || 'value',
    }));

    /** 获取选项的显示文本 */
    const getOptionText = (option: PickerOption): string | number => option[fieldNames.value.text] as string | number;

    /** 获取选项的值 */
    const getOptionValue = (option: PickerOption): string | number => option[fieldNames.value.value] as string | number;

    /** 判断选项是否被禁用 */
    const isOptionDisabled = (option: PickerOption): boolean => !!option.disabled;

    /** ARIA: 当前激活的选项 DOM id，供 aria-activedescendant 使用 */
    const activeDescendantId = computed(() => `picker-col-${props.columnIndex}-opt-${currentIndex.value}`);

    // ==================== 选项高度测量 ====================

    /**
     * 从 DOM 测量单个选项的实际像素高度
     * 使用 getBoundingClientRect 获取亚像素精度，避免 rem 换算导致的对齐偏差
     * 测量结果会被缓存，options 变化时重置
     */
    const measureItemHeight = (): number => {
      if (measuredItemHeight.value > 0) return measuredItemHeight.value;
      const optionClass = n('option');
      const container = scrollRef.value;
      if (container) {
        const firstItem = container.querySelector(`.${optionClass}`) as HTMLElement;
        if (firstItem) {
          measuredItemHeight.value = firstItem.getBoundingClientRect().height;
          return measuredItemHeight.value;
        }
      }
      return props.optionHeight;
    };

    // ==================== 索引查找工具 ====================

    /** 根据值查找对应选项的索引，找不到则回退到 defaultIndex */
    const findIndexByValue = (val: string | number | undefined): number => {
      if (val === undefined) return props.defaultIndex;
      const index = props.options.findIndex(
        opt => getOptionValue(opt) === val,
      );
      return index >= 0 ? index : props.defaultIndex;
    };

    /**
     * 从目标索引出发，查找最近的未禁用选项索引
     * 先向下查找，再向上查找；全部禁用时返回原索引
     */
    const findAvailableIndex = (targetIndex: number): number => {
      const count = props.options.length;
      if (count === 0) return 0;
      const index = Math.max(0, Math.min(targetIndex, count - 1));
      for (let i = index; i < count; i++) {
        if (!props.options[i].disabled) return i;
      }
      for (let i = index - 1; i >= 0; i--) {
        if (!props.options[i].disabled) return i;
      }
      return index;
    };

    // ==================== 事件触发 ====================

    /** 向父组件 emit change 事件，携带选中值、索引、选项和列索引 */
    const emitChange = (index: number) => {
      const option = props.options[index];
      if (option) {
        emit('change', {
          value: getOptionValue(option),
          index,
          option,
          columnIndex: props.columnIndex,
        });
      }
    };

    /**
     * 滚动结束回调（由 useColumnTouch 调用）
     * 跳过禁用选项，更新 currentIndex 并通知父组件
     */
    const handleScrollTo = (index: number) => {
      const availableIndex = findAvailableIndex(index);
      if (availableIndex !== currentIndex.value) {
        currentIndex.value = availableIndex;
        emitChange(availableIndex);
      } else {
        currentIndex.value = availableIndex;
      }
    };

    // ==================== 触摸滚动核心（H5） ====================

    /** H5: 初始化触摸滚动逻辑，管理 offset / duration 和惯性动画 */
    const touch = useColumnTouch({
      getOptionCount: () => props.options.length,
      getItemHeight: () => measureItemHeight(),
      visibleOptionNum: () => props.visibleOptionNum,
      swipeDuration: () => props.swipeDuration,
      onScrollTo: handleScrollTo,
      isDisabled: () => props.disabled,
    });

    /** H5: 选项列表容器的样式，通过 transform + transition 实现滚动和动画 */
    const wrapperStyle = computed(() => {
      const transformValue = `translate3d(0, ${touch.offset.value}px, 0)`;
      const transitionValue = touch.duration.value
        ? `transform ${touch.duration.value}ms ease-out`
        : 'none';
      return {
        transform: transformValue,
        transition: transitionValue,
      };
    });

    // ==================== 交互处理 ====================

    /** 点击选项：跳过禁用项，滚动到目标位置并触发 change */
    const handleClickOption = (option: PickerOption, index: number) => {
      if (props.disabled || option.disabled) return;

      emit('click-option', { option, index });

      const availableIndex = findAvailableIndex(index);
      currentIndex.value = availableIndex;
      touch.scrollToIndex(availableIndex);
      emitChange(availableIndex);
    };

    // ==================== 暴露给父组件的方法 ====================

    /** 滚动到指定索引，immediate 为 true 时无过渡动画 */
    const scrollTo = (index: number, immediate = false) => {
      const availableIndex = findAvailableIndex(index);
      currentIndex.value = availableIndex;
      touch.scrollToIndex(availableIndex, immediate);
    };

    /** 立即停止惯性滚动动画，并对齐到最近选项 */
    const stopMomentum = () => {
      touch.stopMomentum();
    };

    /** 获取当前选中项索引 */
    const getCurrentIndex = (): number => currentIndex.value;

    /**
     * 根据 props.value 初始化列的滚动位置
     * 在挂载、选项变化、弹窗打开时调用
     */
    const initColumn = (immediate = true) => {
      const initialIndex = findIndexByValue(props.value);
      const availableIndex = findAvailableIndex(initialIndex);
      currentIndex.value = availableIndex;
      touch.scrollToIndex(availableIndex, immediate);
    };

    // ==================== 生命周期与侦听器 ====================

    /** 挂载后测量选项高度并初始化滚动位置 */
    onMounted(() => {
      nextTick(() => {
        measureItemHeight();
        initColumn(true);
      });
    });

    /** 外部 value 变化时，滚动到对应选项（仅在索引发生变化时触发 change） */
    watch(
      () => props.value,
      (val) => {
        const index = findIndexByValue(val);
        const availableIndex = findAvailableIndex(index);
        if (availableIndex !== currentIndex.value) {
          currentIndex.value = availableIndex;
          touch.scrollToIndex(availableIndex);
        }
      },
    );

    /** options 列表变化时：重置高度缓存 → 重新测量 → 重新初始化位置 */
    watch(
      () => props.options,
      () => {
        measuredItemHeight.value = 0;
        nextTick(() => {
          measureItemHeight();
          initColumn(false);
        });
      },
    );

    /** optionHeight 变化时：重置高度缓存 → 等 CSS 变量生效后重新测量 → 重新定位 */
    watch(
      () => props.optionHeight,
      () => {
        measuredItemHeight.value = 0;
        nextTick(() => {
          measureItemHeight();
          initColumn(true);
        });
      },
    );

    return {
      n,
      classes,
      scrollRef,
      currentIndex,
      activeDescendantId,
      wrapperStyle,
      getOptionText,
      getOptionValue,
      isOptionDisabled,
      handleClickOption,
      onTouchStart: touch.onTouchStart,
      onTouchMove: touch.onTouchMove,
      onTouchEnd: touch.onTouchEnd,
      scrollTo,
      stopMomentum,
      getCurrentIndex,
    };
  },
});
</script>
